import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import startPlay from '../../webrtc/startPlay';
import stopPlay from '../../webrtc/stopPlay';

export const PUBLISH_PLAYER = "PUBLISH_PLAYER";
const PEER_PLAYER = "PEER_PLAYER";

const PeerPlayer = (props) => {
  const videoElement = useRef(null);
  const publishSettings = useSelector((state) => state.publishSettings);
  const [connected, setConnected] = useState(false);
  const [audioTrack, setAudioTrack] = useState();
  const [showPlayButton, setShowPlayButton] = useState(false);
  const [videoTrack, setVideoTrack] = useState();
  const peerConnection = useRef(null);

  const playerType = (props.streamName === publishSettings.streamName) ? PUBLISH_PLAYER : PEER_PLAYER;

  useEffect(() => {
    let mounted = true;

    if (playerType === PUBLISH_PLAYER) {
      console.log("hop: publish player mounted");
      // For PUBLISH_PLAYER in 'hop', use the tracks from publishSettings (which come from the compositor)
      if (publishSettings.videoTrack || publishSettings.audioTrack) {
        const newLocalStream = new MediaStream();
        if (publishSettings.videoTrack) newLocalStream.addTrack(publishSettings.videoTrack);
        if (publishSettings.audioTrack) newLocalStream.addTrack(publishSettings.audioTrack);

        if (videoElement.current) {
          videoElement.current.muted = true; // Mute local preview
          videoElement.current.srcObject = newLocalStream;
        }
      } else {
        if (videoElement.current) {
          videoElement.current.srcObject = null;
        }
      }
    } else { // PEER_PLAYER
      console.log("hop: peer player mounted for stream:", props.streamName);
      let playSettings = {
        signalingURL: publishSettings.signalingURL,
        applicationName: publishSettings.applicationName,
        streamName: props.streamName,
      }
      startPlay(playSettings, null, {
        onSetPeerConnection: (result) => {
          if (mounted) {
            peerConnection.current = result.peerConnection;
          }
        },
        onPeerConnectionOnTrack: (event) => {
          console.log("hop: getting peer tracks for", props.streamName, event.track.kind);
          if (mounted && event.track && event.track.kind) {
            if (event.track.kind === 'audio') {
              setAudioTrack(event.track);
            } else if (event.track.kind === 'video') {
              setVideoTrack(event.track);
            }
          }
        },
        onConnectionStateChange: (result) => {
          console.log("hop: peer connection state for", props.streamName, result.connected);
          if (mounted) {
            setConnected(result.connected);
          }
        },
        onError: (e) => {
          console.log("hop: peer player error for", props.streamName, e);
        }
      });
    }

    return () => {
      mounted = false; // Prevent state updates on unmounted component
      if (playerType === PEER_PLAYER && peerConnection.current) {
        console.log("hop: stopping peer player for stream:", props.streamName);
        stopPlay(peerConnection.current, null, {});
        peerConnection.current = null;
      }
    };
  }, [
    playerType,
    publishSettings.streamName, // Dependency for playerType
    publishSettings.signalingURL,
    publishSettings.applicationName,
    publishSettings.videoTrack, // For local preview update
    publishSettings.audioTrack, // For local preview update
    props.streamName,
  ]);

  useEffect(() => {
    let mounted = true;

    if (playerType === PEER_PLAYER) {
      if (connected && mounted) {
        const newStream = new MediaStream();
        if (audioTrack) newStream.addTrack(audioTrack);
        if (videoTrack) newStream.addTrack(videoTrack);

        if (videoElement.current) {
          videoElement.current.srcObject = newStream;
          videoElement.current.muted = true; // Mute for autoplay on iOS
          videoElement.current.play().catch(error => {
            if (error.name === 'NotAllowedError') {
              setShowPlayButton(true);
            }
          });
        }
      } else if (!connected && mounted) {
        // Clear srcObject if disconnected
        if (videoElement.current) {
          videoElement.current.srcObject = null;
        }
      }
    }
    return () => { mounted = false; };
  }, [connected, playerType, audioTrack, videoTrack, props.streamName]);

  const toggleFullScreen = () => {
    if (videoElement.current) {
      if (videoElement.current.requestFullscreen) {
        videoElement.current.requestFullscreen();
      } else if (videoElement.current.mozRequestFullScreen) { /* Firefox */
        videoElement.current.mozRequestFullScreen();
      } else if (videoElement.current.webkitRequestFullscreen) { /* Chrome, Safari and Opera */
        videoElement.current.webkitRequestFullscreen();
      } else if (videoElement.current.msRequestFullscreen) { /* IE/Edge */
        videoElement.current.msRequestFullscreen();
      }
    }
  };

  const handlePlay = () => {
    if (videoElement.current) {
      videoElement.current.play().then(() => {
        setShowPlayButton(false);
      }).catch(e => {
        console.error("Error trying to play video manually for stream " + props.streamName, e);
      });
    }
  };

  return(
    <div className={`hop-peer-player-container ${
      playerType === PUBLISH_PLAYER ? 'local-player-container' : 'remote-player-container'
    }`}
    >
      <div className="video-container">
        {showPlayButton && <button className="play-button" onClick={handlePlay}>&#9658;</button>}
        <video id={props.streamName} className="hop-meeting-peer-player" ref={videoElement} autoPlay playsInline ></video>
        <button className="fullscreen-button" onClick={toggleFullScreen}>Fullscreen</button>
      </div>
      <div className="hop-peer-player-name">{props.streamName}</div>
    </div>
  )
}
export default PeerPlayer
